import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types/product';
import { formatPrice } from '../utils/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faShoppingCart, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import { isOutOfStock } from '../utils/products';

interface ProductCardProps {
  product: Product;
  variant?: 'default' | 'featured' | 'bestseller';
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variant = 'default'
}) => {
  const { addToCart } = useCart();
  const {
    id,
    name,
    description,
    price,
    currency,
    rating,
    reviews,
    badge,
    images,
    isSubscription,
    shortDescription
  } = product;

  const getImageSrc = (imagePath: string | undefined) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    try {
      return require(`../assets/images/${imagePath}`);
    } catch {
      return imagePath;
    }
  };

  const handleAddToCart = () => {
    if (!isOutOfStock(product)) {
      addToCart(product);
    }
  };

  const outOfStock = isOutOfStock(product);

  const getCardClasses = () => {
    switch (variant) {
      case 'bestseller':
        return {
          container: "group bg-white rounded-3xl overflow-hidden border-2 border-[#2b603e]/20 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col h-full transform hover:-translate-y-2",
          imageContainer: "relative overflow-hidden h-80",
          badge: "absolute top-4 left-4 bg-[#2b603e] text-white px-4 py-2 rounded-full text-sm font-bold z-20 shadow-md"
        };
      case 'featured':
        return {
          container: "group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full transform hover:-translate-y-2",
          imageContainer: "relative overflow-hidden h-64 md:h-80",
          badge: "absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold z-20 shadow-md"
        };
      default:
        return {
          container: "group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full transform hover:-translate-y-2",
          imageContainer: "relative overflow-hidden h-64 md:h-80",
          badge: "absolute top-4 right-4 bg-[#2b603e] text-white px-4 py-2 rounded-full text-sm font-bold z-20 shadow-md"
        };
    }
  };

  const cardClasses = getCardClasses();

  return (
    <div className={cardClasses.container}>
      <div className={cardClasses.imageContainer}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20 z-10"></div>
        <img
          src={getImageSrc(images?.[0])}
          alt={name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {(badge || isSubscription || outOfStock) && (
          <div className={cardClasses.badge}>
            {outOfStock ? 'Out of Stock' : (badge || (isSubscription ? 'Subscription' : ''))}
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <Link
          to={`/product/${id}`}
          className="text-xl font-bold mb-2 text-gray-800 text-left truncate hover:text-[#2b603e] transition-colors"
        >
          {name}
        </Link>
        <p className="text-gray-600 mb-4 flex-grow line-clamp-2 text-sm">
          {shortDescription || description || 'Premium quality product'}
        </p>

        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-[#2b603e]">{formatPrice(price)}</span>
            {isSubscription && (
              <span className="text-xs text-gray-500">per month</span>
            )}
          </div>
          <div className="flex items-center text-sm">
            <FontAwesomeIcon icon={faStar} className="text-yellow-500 mr-1" />
            <span className="text-gray-600">{rating} ({reviews})</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleAddToCart}
            disabled={outOfStock}
            className={`font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 ${outOfStock
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-[#2b603e] hover:bg-[#1a3d25] text-white hover:shadow-md'
              }`}
          >
            <FontAwesomeIcon icon={faShoppingCart} />
            <span>{outOfStock ? 'Out of Stock' : (isSubscription ? 'Subscribe' : 'Add to Cart')}</span>
          </button>
          <Link
            to={`/product/${id}`}
            className="bg-white border border-[#2b603e] text-[#2b603e] hover:bg-[#2b603e] hover:text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 hover:shadow-md"
          >
            <span>Details</span>
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;